import { useCallback, useState } from 'react';
import { Technician, TechnicianStatus } from '@/types';
import type { MapLocation } from '@/components/ui/MultiMarkerMap';

const TECHNICIAN_LOCATION_PREFIX = 'technician-';

/**
 * Remembers technician addresses the map couldn't geocode, so those technicians can be shown
 * unchecked with a "can't be located" note. Keyed by address, so fixing the address clears it.
 * Feed `reportUnresolved` to MultiMarkerMap's `onUnresolved`.
 */
export function useUnlocatableTechnicianAddresses(technicians: Technician[] | undefined) {
  const [unlocatable, setUnlocatable] = useState<Set<string>>(new Set());
  const reportUnresolved = useCallback(
    (locationIds: string[]) => {
      const addresses = locationIds
        .filter((id) => id.startsWith(TECHNICIAN_LOCATION_PREFIX))
        .map((id) => technicians?.find((t) => t.id === id.slice(TECHNICIAN_LOCATION_PREFIX.length))?.address?.trim())
        .filter((a): a is string => !!a);
      if (!addresses.length) return;
      setUnlocatable((prev) => (addresses.every((a) => prev.has(a)) ? prev : new Set([...prev, ...addresses])));
    },
    [technicians],
  );
  const isUnlocatable = useCallback(
    (technician: Technician) => {
      const address = technician.address?.trim();
      return !!address && unlocatable.has(address);
    },
    [unlocatable],
  );
  return { isUnlocatable, reportUnresolved };
}

/**
 * Map marker for a technician's stored address (the "Address" mode of the map technician
 * layer). Null when there's no address. Geocoded like a job address, so cached per browser.
 */
export function technicianAddressLocation(technician: Technician): MapLocation | null {
  const address = technician.address?.trim();
  if (!address) return null;
  return {
    id: `${TECHNICIAN_LOCATION_PREFIX}${technician.id}`,
    type: 'technician',
    address,
    label: technician.name,
    subtitle: address,
    details: ['Technician address'],
    isHome: true,
  };
}

type SelectedTechnicianIds = string | string[] | null | undefined;

export interface TechnicianLike {
  id: string;
  name: string;
  status?: TechnicianStatus;
}

interface LeadingOption {
  value: string;
  label: string;
}

function toSelectedSet(selectedTechnicianIds: SelectedTechnicianIds): Set<string> {
  if (!selectedTechnicianIds) {
    return new Set<string>();
  }

  if (Array.isArray(selectedTechnicianIds)) {
    return new Set(selectedTechnicianIds.filter(Boolean));
  }

  return selectedTechnicianIds ? new Set([selectedTechnicianIds]) : new Set<string>();
}

export function isDeletedTechnician(status?: TechnicianStatus): boolean {
  return status === 'Deleted';
}

export function isActiveTechnician(status?: TechnicianStatus): boolean {
  return status === 'Active' || status === undefined;
}

export function isInactiveTechnician(status?: TechnicianStatus): boolean {
  return !isActiveTechnician(status);
}

export function formatTechnicianStatus(status?: TechnicianStatus): string {
  switch (status) {
    case 'OnLeave':
      return 'On Leave';
    case 'Deleted':
      return 'Deleted';
    case 'Active':
    default:
      return 'Active';
  }
}

export function formatTechnicianOptionLabel(name: string, status?: TechnicianStatus): string {
  if (isActiveTechnician(status)) {
    return name;
  }

  return `${name} (${formatTechnicianStatus(status)})`;
}

export function hasInactiveTechnicians<T extends TechnicianLike>(technicians?: T[]): boolean {
  return (technicians ?? []).some((technician) => isInactiveTechnician(technician.status));
}

export function filterTechniciansForSelector<T extends TechnicianLike>(
  technicians: T[] | undefined,
  showInactive: boolean,
  selectedTechnicianIds?: SelectedTechnicianIds,
): T[] {
  const selectedSet = toSelectedSet(selectedTechnicianIds);

  return (technicians ?? []).filter((technician) => (
    showInactive ||
    isActiveTechnician(technician.status) ||
    selectedSet.has(technician.id)
  ));
}

export function buildTechnicianSelectOptions<T extends TechnicianLike>(
  technicians: T[] | undefined,
  showInactive: boolean,
  selectedTechnicianIds?: SelectedTechnicianIds,
  leadingOption?: LeadingOption,
): Array<{ value: string; label: string }> {
  const technicianOptions = filterTechniciansForSelector(
    technicians,
    showInactive,
    selectedTechnicianIds,
  ).map((technician) => ({
    value: technician.id,
    label: formatTechnicianOptionLabel(technician.name, technician.status),
  }));

  return leadingOption ? [leadingOption, ...technicianOptions] : technicianOptions;
}